import React, { useRef } from 'react';
import styled from '@emotion/styled';
import Loader from '../../../../components/general/Loader';
import TemplateFormFieldModel from '../../../../models/templating/api/TemplateFormFieldModel';
import {
    ui, ActionButton, ButtonRow, EmptyGroup, FooterNote, Group, GroupCaption, GroupFooter, GroupSection, GroupTitle,
    Row, RowDetail, RowText, RowTitle, SelectionCheck
} from './ManagementUI';

export interface ImportedLocationChanges {
    locationId: string;
    locationName: string;
    fieldChanges: { fieldTitle: string; newValue: string }[];
}

interface LocationTopicsToolProps {
    isLoading: boolean;
    hasSharedTopicsApp: boolean;
    fields: TemplateFormFieldModel[];
    selectedFields: Set<string>;
    onToggleField: (fieldTitle: string) => void;
    onToggleAllFields: () => void;
    selectedLocationCount: number;
    onExport: () => void;
    onImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isImporting: boolean;
    isApplying: boolean;
    importProgress?: string;
    importedData: ImportedLocationChanges[] | null;
    onApplyImport: () => void;
}

const PREVIEW_LIMIT = 10;

// Export and import location-specific Shared Topics fields as an Excel spreadsheet
const LocationTopicsTool: React.FC<LocationTopicsToolProps> = ({
    isLoading,
    hasSharedTopicsApp,
    fields,
    selectedFields,
    onToggleField,
    onToggleAllFields,
    selectedLocationCount,
    onExport,
    onImport,
    isImporting,
    isApplying,
    importProgress,
    importedData,
    onApplyImport
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (isLoading) {
        return <LoadingGroup><Loader /></LoadingGroup>;
    }
    if (!hasSharedTopicsApp) {
        return <EmptyGroup>No Shared Topics application found in this organization.</EmptyGroup>;
    }
    if (fields.length === 0) {
        return <EmptyGroup>No location-specific topics configured yet.</EmptyGroup>;
    }

    const allFieldsSelected = selectedFields.size === fields.length;

    return (
        <>
            <GroupSection>
                <GroupTitle>Topics</GroupTitle>
                <Group>
                    <Row>
                        <RowText>
                            <RowTitle>{selectedFields.size} of {fields.length} topics selected</RowTitle>
                        </RowText>
                        <ActionButton variant="plain" text={allFieldsSelected ? 'Deselect All' : 'Select All'} onClick={onToggleAllFields} />
                    </Row>
                    <TopicList>
                        {fields.map((field, index) => (
                            <TopicRow key={`${field.id}-${index}`} onClick={() => onToggleField(field.title)}>
                                <SelectionCheck
                                    type="checkbox"
                                    aria-label={field.title}
                                    checked={selectedFields.has(field.title)}
                                    onChange={() => onToggleField(field.title)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <RowText>
                                    <RowTitle>{field.title}</RowTitle>
                                    {(field.name || field.tip) && (
                                        <RowDetail>{[field.name, field.tip].filter(Boolean).join(' · ')}</RowDetail>
                                    )}
                                </RowText>
                            </TopicRow>
                        ))}
                    </TopicList>
                </Group>
            </GroupSection>

            <GroupSection>
                <GroupTitle>Spreadsheet</GroupTitle>
                <Group>
                    <Row>
                        <RowText>
                            <RowTitle>Export to Excel</RowTitle>
                            <RowDetail>Download the selected topics for the selected locations.</RowDetail>
                        </RowText>
                        <ActionButton
                            variant="primary"
                            text="Export"
                            onClick={onExport}
                            disabled={selectedLocationCount === 0 || selectedFields.size === 0 || isImporting || isApplying}
                        />
                    </Row>
                    <Row>
                        <RowText>
                            <RowTitle>Import from Excel</RowTitle>
                            <RowDetail>Use a file with "Location ID" and "Location Name" columns followed by topic columns. Exporting first gives you the right format.</RowDetail>
                        </RowText>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={onImport}
                            style={{ display: 'none' }}
                            disabled={isImporting || isApplying}
                        />
                        <ActionButton
                            text={isImporting ? 'Importing…' : 'Choose File…'}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isImporting || isApplying}
                        />
                    </Row>
                    {importProgress && (
                        <GroupFooter>
                            <FooterNote>{importProgress}</FooterNote>
                        </GroupFooter>
                    )}
                </Group>
                <GroupCaption>
                    {selectedLocationCount === 0
                        ? 'Select locations from the list to export.'
                        : `Exports ${selectedLocationCount} ${selectedLocationCount === 1 ? 'location' : 'locations'} × ${selectedFields.size} ${selectedFields.size === 1 ? 'topic' : 'topics'}.`}
                </GroupCaption>
            </GroupSection>

            {importedData && importedData.length > 0 && (
                <GroupSection>
                    <GroupTitle>Review Imported Changes</GroupTitle>
                    <Group>
                        {importedData.slice(0, PREVIEW_LIMIT).map((location, index) => (
                            <PreviewRow key={index}>
                                <PreviewHeader>
                                    <RowTitle>{location.locationName}</RowTitle>
                                    <RowDetail>{location.locationId}</RowDetail>
                                </PreviewHeader>
                                {location.fieldChanges.map((change, changeIndex) => (
                                    <PreviewChange key={changeIndex}>
                                        <PreviewLabel>{change.fieldTitle}</PreviewLabel>
                                        <PreviewValue>{change.newValue}</PreviewValue>
                                    </PreviewChange>
                                ))}
                            </PreviewRow>
                        ))}
                        <GroupFooter>
                            <FooterNote>
                                {importedData.length} {importedData.length === 1 ? 'location has' : 'locations have'} pending changes
                                {importedData.length > PREVIEW_LIMIT ? ` (showing first ${PREVIEW_LIMIT})` : ''}
                            </FooterNote>
                            <ButtonRow>
                                <ActionButton
                                    variant="primary"
                                    text={isApplying ? 'Applying…' : `Apply to ${importedData.length} ${importedData.length === 1 ? 'Location' : 'Locations'}`}
                                    onClick={onApplyImport}
                                    disabled={isApplying || isImporting}
                                />
                            </ButtonRow>
                        </GroupFooter>
                    </Group>
                </GroupSection>
            )}
        </>
    );
};

// Loader is absolutely positioned, so the card needs to be its positioning context
const LoadingGroup = styled(EmptyGroup)`
    position: relative;
    min-height: 160px;
`;

const TopicList = styled.div`
    max-height: 360px;
    overflow-y: auto;

    & > * + * {
        border-top: 0.5px solid ${ui.separator};
    }
`;

const TopicRow = styled(Row)`
    cursor: pointer;

    &:hover {
        background: rgba(118, 118, 128, 0.06);
    }
`;

const PreviewRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
`;

const PreviewHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

const PreviewChange = styled.div`
    display: grid;
    grid-template-columns: minmax(120px, 200px) minmax(0, 1fr);
    gap: 12px;
    font-size: 13px;
`;

const PreviewLabel = styled.span`
    color: ${ui.secondaryLabel};
`;

const PreviewValue = styled.span`
    color: ${ui.label};
    word-break: break-word;
`;

export default LocationTopicsTool;
